import { createLogger, Logger } from '../../../../../../shared/helpers/loggerFacade';
import { FrameCache } from './frame-cache';
import { RvfcStatsLogger } from './rvfc-stats-logger';
import { VideoFrameTracker } from './video-frame-tracker';

const logger = createLogger('room-agent-recorder');

/**
 * Оптимизация отрисовки room-agent: пропуск drawImage для участников
 * без новых видеокадров (requestVideoFrameCallback).
 *
 * Объединяет dirty-tracking, кэш OffscreenCanvas и статистику в один фасад.
 * Используется ТОЛЬКО в room-agent — локальная запись и транслятор не затронуты.
 *
 * Включается через enableRvfcSkip() при старте записи, если флаг enable_rvfc_skip активен.
 * Очищается через clearRvfcSkip() при завершении room-agent.
 */
export class RvfcSkipOptimizer {
    private readonly _tracker: VideoFrameTracker;
    private readonly _cache: FrameCache;
    private readonly _stats: RvfcStatsLogger;
    private _dirtySet: Set<HTMLVideoElement> = new Set();

    constructor(optimizerLogger: Logger) {
        this._cache = new FrameCache();
        // При удалении видео из DOM — чистим запись кэша OffscreenCanvas
        this._tracker = new VideoFrameTracker(true, (video) => this._cache.remove(video));
        this._stats = new RvfcStatsLogger(optimizerLogger);
        optimizerLogger.debug('rVFC skip enabled, tracker started');
    }

    /**
     * Подготовка кадра: проверка смены раскладки и получение dirty-множества.
     * Вызывается в начале drawConference перед отрисовкой участников.
     */
    beforeDraw(
        participantCount: number,
        activeGridView: string,
        currentPage: number,
        isScreenSharingActive: boolean
    ): void {
        this._tracker.checkLayoutChange(participantCount, activeGridView, currentPage, isScreenSharingActive);
        this._dirtySet = this._tracker.consumeDirty();
    }

    /** Множество dirty-видео текущего кадра (валидно после beforeDraw) */
    getDirtySet(): ReadonlySet<HTMLVideoElement> {
        return this._dirtySet;
    }

    /** Кэш OffscreenCanvas */
    getFrameCache(): FrameCache {
        return this._cache;
    }

    /**
     * Аккумуляция статистики и периодическое логирование.
     * Вызывается в конце drawConference после отрисовки всех участников.
     */
    afterDraw(participantCount: number): void {
        this._stats.accumulate(participantCount, this._dirtySet.size);
        this._stats.maybeLog(this._tracker.trackedCount());
    }

    destroy(): void {
        this._tracker.destroy();
        this._cache.destroy();
    }
}

// Module-scope state по паттерну overlayManager в draw-conference-cached.ts
let rvfcOptimizer: RvfcSkipOptimizer | null = null;

/** Включить оптимизацию пропуска неизменившихся кадров. Только для room-agent. */
export function enableRvfcSkip() {
    rvfcOptimizer = new RvfcSkipOptimizer(logger);
}

/** Очистка ресурсов. Вызывается из roomAgentSaga рядом с clearOverlayManager(). */
export function clearRvfcSkip() {
    rvfcOptimizer?.destroy();
    rvfcOptimizer = null;
}

/** Текущий экземпляр оптимизатора (null если флаг enable_rvfc_skip выключен) */
export function getRvfcOptimizer(): RvfcSkipOptimizer | null {
    return rvfcOptimizer;
}
